/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState } from 'react';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import {
  Horse, NutritionReport, Feed, Nutrients, SpecialNutritionalBounds, User, EditableReportSection,
} from '../../redux/types';
import { NutrientRequirementsOutput } from '../../helpers/nutrientRequirementWrapper';
import ReportBox, { NutrientRowItem } from './ReportBox';
import { HorseReportStarchInfoSmall } from '../viewReport/HorseReportStarchInfo';
import DietHorseInfo from './DietHorseInfo';
import DietHorseFeeds from './DietHorseFeeds';
import NutritionOverviewGraph from '../NutritionOverviewGraph';
import getDateAsString from '../../helpers/dateHelper';
import NutritionContributorsGraph from '../NutritionContributorsGraph';
import DietHorseTable from './DietHorseTable';
import NutritionMacrosBreakdownGraph2 from '../NutritionMacrosBreakdownGraph2';
import { HorseReportFibreInfo2 } from '../viewReport/HorseReportFibreInfo';
import { HorseReportProteinInfoSmall } from '../viewReport/HorseReportProteinInfo';
import HorseReportSugarInfo from '../viewReport/HorseReportSugarInfo';
import { HorseReportFatsInfoSmall } from '../viewReport/HorseReportFatsInfo';
import { HorseDietRatiosSmall } from '../viewReport/HorseDietRatios';
import HorseFooterBranding from '../viewReport/HorseFooterBranding';
import { DISCALIMER_TEXT, BrandingID, displayBrandInformation } from './ReportConstants';
import EditableReportSectionComponent, { EditReportSectionDialog } from '../EditableReportSectionComponent';
import { commentsSectionTemplate } from '../../helpers/editableReportSectionsHelper';


interface Props {
  horse: Horse
  report: NutritionReport
  feeds: Map<string, Feed>
  combinedNetNutrients: Nutrients
  currentHorseRequirements: NutrientRequirementsOutput
  feedsAsDM: Nutrients
  contributors: [string, Nutrients][] | undefined
  percentages: Nutrients
  specialBounds: SpecialNutritionalBounds
  roughageAmount: number
  user: User
  brandingID: BrandingID
  totalFeedAmount: number
}

export const A4_PAGE: CSSProperties = {
  fontSize: '14px',
  minHeight: '1042px',
  height: '1042px',
  maxHeight: '1042px',
  width: '695px',
  minWidth: '695px',
  maxWidth: '695px',
  flexDirection: 'column',
  display: 'flex',
  fontVariantLigatures: 'none',
  backgroundColor: 'white',
};

const PAGE_NUMBER: CSSProperties = {
  position: 'relative',
  top: '1022px',
  left: '635px',
  fontSize: '12px',
};

const Report : React.FC<Props> = (props: Props) => {
  const commentsReportSection: EditableReportSection = props.report.editableReportSections.get(3)
    ? props.report.editableReportSections.get(3) as EditableReportSection
    : { content: `${commentsSectionTemplate}`, sectionID: 3 };
  const [editDialogIsOpen, setEditDialogIsOpen] = useState(false);
  const [editDialogContent, setEditDialogContent] = useState('');
  const [editDialogSID, setEditDialogSID] = useState(-1);
  const brand = displayBrandInformation(props.brandingID);
  return (
    <div>
      <EditReportSectionDialog
        key={editDialogSID}
        isOpen={editDialogIsOpen}
        setOpen={setEditDialogIsOpen}
        editorContent={editDialogContent}
        editableSectionID={editDialogSID}
      />
      <div id="page-1s" style={A4_PAGE}>
        <div style={PAGE_NUMBER}>Page 1</div>
        <img src={brand.headerImage} alt="branding" style={{ height: '143px', width: '650px', margin: '20px', marginTop: '0' }} />
        <div style={{ textAlign: 'center', marginBottom: 'auto' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bolder' }}>{props.report.title}</div>
          <div style={{ fontSize: '18px' }}>{getDateAsString(new Date())}</div>
        </div>
        <div style={{ display: 'flex' }}>
          <ReportBox
            title="Horse Profile"
            fabIcon="info"
            fabColor="#7fd985"
            child={(
              <DietHorseInfo
                horse={props.horse}
              />
            )}
          />
          <ReportBox
            title="Feed Intake"
            fabIcon="feed"
            fabColor="#d0cd6a"
            style={{
              marginTop: '0', marginBottom: '0', marginRight: '0', marginLeft: '2em',
            }}
            child={(
              <DietHorseFeeds
                feeds={props.feeds}
                report={props.report}
              />
            )}
          />
        </div>
        <ReportBox
          noMargin
          title="Nutrient Overview"
          fabIcon="report"
          fabColor="#a176c2"
          child={(
            <div style={{ margin: 'auto', width: '80%' }}>
              <NutritionOverviewGraph
                height={250}
                percentages={props.percentages}
                specialBounds={props.specialBounds}
              />
            </div>
          )}
        />
      </div>
      <div id="page-2s" style={A4_PAGE}>
        <div style={PAGE_NUMBER}>Page 2</div>
        {props.contributors ? (
          <ReportBox
            noMargin
            title="Feed Contributors"
            fabIcon="chart"
            fabColor="#ecbdec"
            child={(
              <div style={{ margin: 'auto', width: '80%' }}>
                <NutritionContributorsGraph
                  height={250}
                  contributors={props.contributors}
                />
              </div>
            )}
          />
        ) : ''}
        <ReportBox
          noMargin
          title="Table of Nutrients"
          fabIcon="scales"
          fabColor="#76bdbd"
          child={(
            <DietHorseTable
              combinedNetNutrients={props.combinedNetNutrients}
              currentHorseRequirements={props.currentHorseRequirements}
              specialBounds={props.specialBounds}
            />
          )}
        />
      </div>
      <div id="page-3s" style={A4_PAGE}>
        <div style={PAGE_NUMBER}>Page 3</div>
        <ReportBox
          title=""
          fabIcon=""
          fabColor=""
          child={(
            <>
              <NutritionMacrosBreakdownGraph2
                contributors={props.combinedNetNutrients}
              />
              <div style={{ fontSize: '18px', fontWeight: 'bolder', marginLeft: '71px' }}>Macronutrients</div>
              <NutrientRowItem
                title=""
                fabIcon="pastures"
                fabColor="#88e78d"
                child={(
                  <HorseReportFibreInfo2
                    horse={props.horse}
                    roughageAmount={props.roughageAmount}
                  />
                )}
              />
              <NutrientRowItem
                title=""
                fabIcon="grain"
                fabColor="#d8d57d"
                child={(
                  <HorseReportStarchInfoSmall
                    horse={props.horse}
                    contributors={props.combinedNetNutrients}
                  />
                )}
              />
              <NutrientRowItem
                title=""
                fabIcon="logistics"
                fabColor="#b091c7"
                child={(
                  <HorseReportProteinInfoSmall
                    contributors={props.combinedNetNutrients}
                  />
                )}
              />
              <NutrientRowItem
                title=""
                fabIcon="honey"
                fabColor="#76bdbd"
                child={(
                  <HorseReportSugarInfo
                    small
                    contributors={props.combinedNetNutrients}
                  />
                )}
              />
              <NutrientRowItem
                title=""
                fabIcon="sunflowerb"
                fabColor="#ecbdec"
                child={(
                  <HorseReportFatsInfoSmall
                    contributors={props.combinedNetNutrients}
                  />
                )}
              />
            </>
          )}
        />
      </div>
      <div id="page-4s" style={A4_PAGE}>
        <div style={PAGE_NUMBER}>Page 4</div>
        <ReportBox
          title=""
          fabIcon=""
          fabColor=""
          child={(
            <>
              <HorseDietRatiosSmall
                feedsAsDM={props.feedsAsDM}
                totalFeedAmount={props.totalFeedAmount}
                combinedNetNutrients={props.combinedNetNutrients}
              />
            </>
          )}
        />
        <ReportBox
          title="Report Highlights"
          fabIcon="idea"
          fabColor="#81d885"
          child={(
            <>
              <EditableReportSectionComponent
                isNewReport
                sectionTitle="Report Highlights and Additional Comments"
                editableReportSection={commentsReportSection}
                setEditDiaogOpen={() => {
                  setEditDialogContent(commentsReportSection.content);
                  setEditDialogSID(commentsReportSection.sectionID);
                  setEditDialogIsOpen(true);
                }}
              />
            </>
          )}
        />
        <p style={{ fontSize: '10px' }}>
          <b>DISCLAIMER: </b>
          {DISCALIMER_TEXT}
        </p>
        <div style={{ marginTop: 'auto' }}>
          <HorseFooterBranding
            user={props.user}
            brandingInfo={{
              name: brand.name,
              location: brand.address,
              socialMedia: brand.socialMedia,
              logoBottom: brand.logo,
            }}
          />
        </div>
      </div>
    </div>
  );
};


export default Report;
